import * as fragments from 'src/graphql/fragments'
import * as queries from 'src/graphql/queries'
import { ListQuery } from 'src/graphql/ListQuery'
import { PaginatedQuery } from 'src/graphql/PaginatedQuery'
import { InfiniteQuery } from 'src/graphql/InfiniteQuery'
import { Query } from './Query'
import { Set } from 'immutable'
import { isNotNullOrUndefined, objectSize } from 'src/helpers'
import moment from 'moment'
import { IsUnresolved } from 'src/utils/Future'

export const campaignsPageSize = 9

type Tokens = {
  matchAny: (...values: (string | null | undefined)[]) => boolean
}

const tokens = (search: string | null | undefined): Tokens => {
  if (!search) {
    return {
      matchAny: () => true,
    }
  }

  const searchTokens = search.toLowerCase().split(' ')

  return {
    matchAny: (...values) => {
      const valueTokens = Set(
        values
          .filter(isNotNullOrUndefined)
          .flatMap(value => value.toLowerCase().split(' ')),
      )
      return searchTokens.every(searchToken =>
        valueTokens.contains(searchToken),
      )
    },
  }
}

const descendingIntegerId = (id: string): string =>
  id
    .padStart(20, '0')
    .split('')
    .map(digit => parseInt(digit, 10))
    .map(digit => 9 - digit)
    .map(digit => digit.toString())
    .join('')

const digits = '0123456789'.split('')

const descendingDateTime = (dateTime: string): string =>
  dateTime
    .split('')
    .map(character =>
      digits.includes(character)
        ? (9 - parseInt(character, 10)).toString()
        : character,
    )
    .join('')

const dateIsBetween = (
  date: fragments.OptionalYearDateFragment | null | undefined,
  start: string | null | undefined,
  end: string | null | undefined,
) =>
  !start ||
  !end ||
  (date &&
    moment({
      day: date.day,
      month: date.month,
      year: date.year ?? undefined,
    }).isBetween(moment(start), moment(end), 'day'))

const contactPredicate = (
  contact: fragments.ContactFragment,
  {
    search,
    contacts,
    groups,
    birthdayStartDate,
    birthdayEndDate,
    anniversaryStartDate,
    anniversaryEndDate,
    showSecondaryContacts,
    hasNoGroup,
  }: queries.GetMinimalContactsVariables,
) => {
  if (
    // No way to do this without querying groups
    groups?.length ||
    // No way to do this without querying groups
    typeof hasNoGroup === 'boolean'
  ) {
    throw new IsUnresolved()
  }
  return [
    tokens(search).matchAny(
      contact.firstName,
      contact.lastName,
      contact.company,
      contact.address1,
      contact.address2,
      contact.city,
      contact.state,
      contact.postalCode,
      contact.country,
    ),
    dateIsBetween(
      contact.anniversary,
      anniversaryStartDate,
      anniversaryEndDate,
    ),
    dateIsBetween(contact.birthday, birthdayStartDate, birthdayEndDate),
    !contacts?.length || contacts.includes(contact.id),
    showSecondaryContacts || contact.isPrimary,
  ].every(p => p)
}

const contactSortKey = ({
  id,
  firstName,
  lastName,
}: fragments.MinimalContactFragment) => `${lastName}${firstName}${id}`

const layoutsPredicate = (
  layout: fragments.DetailedLayoutFragment,
  variables: Omit<queries.GetDetailedLayoutsVariables, 'offset' | 'limit'>,
) =>
  layout.category === variables.category &&
  (variables.owner === undefined || layout.owner === variables.owner) &&
  (variables.dimensions === undefined ||
    layout.dimensions === variables.dimensions)

const layoutsSortKey = ({ id }: fragments.DetailedLayoutFragment) =>
  descendingIntegerId(id)

export * from 'src/graphql/queries'

// WRITE DECLARATIONS IN ALPHABETICAL ORDER

export const getAccount = Query({
  operation: queries.GetAccount,
  mapData: data => data.account,
})

export const getAllContacts = ListQuery({
  operation: queries.GetAllContacts,
  mapData: ({ paginatedContacts: { allContacts } }) => allContacts,
  resultTypename: 'Contact',
  isValidResult: fragments.isMinimalContactFragment,
  filter: () => true,
  orderBy: contactSortKey,
})

export const getContactsIds = ListQuery({
  operation: queries.GetContactsIds,
  mapData: ({ paginatedContacts: { allContacts } }) => allContacts,
  resultTypename: 'Contact',
  isValidResult: fragments.isOnlyIdsContactFragment,
  filter: () => true,
  orderBy: ({ id }) => `${id}`,
})

export const getAllGroups = ListQuery({
  operation: queries.GetAllGroups,
  mapData: ({ groups }) => groups,
  resultTypename: 'Group',
  isValidResult: fragments.isGroupFragment,
  filter: () => true,
  orderBy: ({ id, name }) => `${name}${id}`,
})

export const getAwards = Query({
  operation: queries.GetAwards,
  mapData: data => data.awards,
})

export const getCampaign = Query({
  operation: queries.GetCampaign,
  mapData: data => data.campaign,
})

export const getCampaignOrders = InfiniteQuery({
  operation: queries.GetCampaignOrders,
  mapData: data => data.campaign.orders,
  resultTypename: 'Order',
  pageSize: 1,
  isValidResult: fragments.isDetailedOrderFragment,
  filter: () => true,
  orderBy: ({ createdAt }) => descendingDateTime(createdAt),
})

export const getCard = Query({
  operation: queries.GetCard,
  mapData: data => data.card,
})

export const getCardCatalog = Query({
  operation: queries.GetCatalog,
  mapData: data => data,
})

export const getCardCategory = Query({
  operation: queries.GetCardCategory,
  mapData: data => data.cardCategory,
})

export const getCardCategories = Query({
  operation: queries.GetCardCategories,
  mapData: data => data.cardCategories,
})

export const getCardCollections = Query({
  operation: queries.GetCardCollections,
  mapData: data => data.collections,
})

export const getItemCategory = Query({
  operation: queries.GetItemCategory,
  mapData: data => data.itemCategory,
})

export const getChildren = ListQuery({
  operation: queries.GetChildren,
  mapData: data => data.contact.children,
  resultTypename: 'Contact',
  isValidResult: fragments.isChildContactFragment,
  filter: (contact, variables) => contact.parent?.id === variables.contactId,
  orderBy: contactSortKey,
})

export const getCollection = Query({
  operation: queries.GetCollection,
  mapData: data => data.collection,
})

export const getContact = Query({
  operation: queries.GetContact,
  mapData: data => data.contact,
})

export const getCreditsBalance = Query({
  operation: queries.GetCreditsBalance,
  mapData: data => data.account.credits,
})

export const getCustomCards = InfiniteQuery({
  operation: queries.GetCustomCards,
  mapData: data => data.cards,
  resultTypename: 'Card',
  pageSize: 20,
  isValidResult: fragments.isCustomCardFragment,
  filter: () => true,
  orderBy: ({ id }) => descendingIntegerId(id),
})

export const getDetailedCountries = Query({
  operation: queries.GetDetailedCountries,
  mapData: data => data.detailedCountries,
})

export const getDetailedLayouts = ListQuery({
  operation: queries.GetDetailedLayouts,
  mapData: ({ layouts }) => layouts,
  resultTypename: 'Layout',
  isValidResult: fragments.isDetailedLayoutFragment,
  filter: layoutsPredicate,
  orderBy: layoutsSortKey,
})

export const getDraftedCards = InfiniteQuery({
  operation: queries.GetDrafts,
  mapData: data => data.drafts,
  resultTypename: 'Card',
  pageSize: 20,
  isValidResult: fragments.isDraftFragment,
  filter: () => true,
  orderBy: ({ updatedAt }) => descendingDateTime(updatedAt),
})

export const getFeaturedCards = Query({
  operation: queries.GetFeaturedCards,
  mapData: data => data.featuredCards,
})

export const getFonts = Query({
  operation: queries.GetFonts,
  mapData: data => ({
    fonts: data.fonts.filter(font => !font.isSig),
    signatures: data.fonts.filter(font => font.isSig),
  }),
})

export const getFlags = Query({
  operation: queries.GetFlags,
  mapData: data => data.flags,
})

export const getGiftCategories = Query({
  operation: queries.GetGiftCategories,
  mapData: data => data.giftCategories,
  hitCDNCache: true,
})

export const getGift = Query({
  operation: queries.GetGift,
  mapData: data => data.gift,
})

export const getGifts = Query({
  operation: queries.GetGifts,
  mapData: data => data.gifts,
})

export const getGroup = Query({
  operation: queries.GetGroup,
  mapData: data => data.group,
})

export const getImages = InfiniteQuery({
  operation: queries.GetImages,
  mapData: data => data.images,
  resultTypename: 'Image',
  pageSize: 20,
  isValidResult: fragments.isUserImageFragment,
  filter: () => true,
  orderBy: ({ createdAt }) => descendingDateTime(createdAt),
})

export const getInfiniteDetailedLayouts = InfiniteQuery({
  operation: queries.GetDetailedLayouts,
  mapData: ({ layouts }) => layouts,
  resultTypename: 'Layout',
  isValidResult: fragments.isDetailedLayoutFragment,
  pageSize: 20,
  filter: layoutsPredicate,
  orderBy: layoutsSortKey,
})

export const getItemCategories = Query({
  operation: queries.GetItemCategories,
  mapData: data => data.itemCategories,
  hitCDNCache: true,
})

export const getItems = Query({
  operation: queries.GetItems,
  mapData: data => data.items,
})

export const getLayout = Query({
  operation: queries.GetLayout,
  mapData: data => data.layout,
})

export const getLayouts = ListQuery({
  operation: queries.GetLayouts,
  mapData: ({ layouts }) => layouts,
  resultTypename: 'Layout',
  isValidResult: fragments.isLayoutFragment,
  filter: (layout, variables) =>
    layout.category === variables.category &&
    (variables.owner === undefined || layout.owner === variables.owner) &&
    (variables.dimensions === undefined ||
      layout.dimensions === variables.dimensions),
  orderBy: ({ id }) => descendingIntegerId(id),
})

export const getLegacyPicturePlusCards = InfiniteQuery({
  operation: queries.GetLegacyPicturePlusCards,
  mapData: data => data.legacyPicturePlusCards,
  resultTypename: 'SendableCard',
  pageSize: 20,
  isValidResult: fragments.isMinimalSendableCardFragment,
  filter: () => true,
  orderBy: ({ id }) => descendingIntegerId(id),
})

export const getMarketingBanners = Query({
  operation: queries.GetBanners,
  mapData: data => data.banners,
})

export const getMarketingContent = Query({
  operation: queries.GetMarketingContent,
  mapData: data => data,
  hitCDNCache: true,
})

export const getMinimalContacts = ListQuery({
  operation: queries.GetMinimalContacts,
  mapData: data => data.paginatedContacts.allContacts,
  resultTypename: 'Contact',
  isValidResult: fragments.isMinimalContactFragment,
  filter: (contact, variables) => {
    if (fragments.isContactFragment(contact)) {
      return contactPredicate(contact, variables)
    } else if (objectSize(variables) !== 0) {
      throw new IsUnresolved()
    } else {
      return true
    }
  },
  orderBy: contactSortKey,
})

export const getInfiniteMinimalContacts = InfiniteQuery({
  operation: queries.GetPaginatedMinimalContacts,
  mapData: data => data.paginatedContacts.contacts,
  resultTypename: 'Contact',
  pageSize: 20,
  isValidResult: fragments.isMinimalContactFragment,
  filter: (contact, variables) => {
    if (fragments.isContactFragment(contact)) {
      return contactPredicate(contact, variables)
    } else if (objectSize(variables) !== 0) {
      throw new IsUnresolved()
    } else {
      return true
    }
  },
  orderBy: contactSortKey,
})

export const getNotificationCount = Query({
  operation: queries.GetNotificationCount,
  mapData: data => data.notificationCount,
})

export const getNotifications = ListQuery({
  operation: queries.GetNotifications,
  mapData: ({ notifications }) => notifications,
  resultTypename: 'Notification',
  isValidResult: fragments.isNotificationFragment,
  filter: (notification, variables) => {
    if (fragments.isNotificationFragment(notification)) {
      return true
    } else if (objectSize(variables) !== 0) {
      throw new IsUnresolved()
    } else {
      return true
    }
  },
  orderBy: ({ createdAt }) => createdAt,
})

export const getPaginatedCampaigns = PaginatedQuery({
  operation: queries.GetPaginatedCampaigns,
  mapData: ({ paginatedCampaigns: { campaigns: results, count } }) => ({
    results,
    count,
  }),
  resultTypename: 'Campaign',
  pageSize: campaignsPageSize,
  isValidResult: fragments.isMinimalCampaignFragment,
  filter: (campaign, { search, shareable }) => {
    if (shareable !== undefined && shareable !== campaign.isShareable) {
      return false
    }

    return tokens(search).matchAny(campaign.name)
  },
  orderBy: ({ createdAt }) => createdAt,
})

export const getOrder = Query({
  operation: queries.GetOrder,
  mapData: data => data.order,
})

export const getPaginatedContacts = PaginatedQuery({
  operation: queries.GetPaginatedContacts,
  mapData: ({ paginatedContacts: { contacts: results, count } }) => ({
    results,
    count,
  }),
  resultTypename: 'Contact',
  pageSize: 20,
  isValidResult: fragments.isContactFragment,
  filter: contactPredicate,
  orderBy: contactSortKey,
})

export const getPaginatedDraftedOrders = PaginatedQuery({
  operation: queries.GetDraftedOrders,
  mapData: ({ paginatedOrders: { orders: results, count } }) => ({
    results,
    count,
  }),
  resultTypename: 'Order',
  pageSize: 20,
  isValidResult: fragments.isMinimalOrderFragment,
  filter: () => true,
  orderBy: ({ updatedAt }) => descendingDateTime(updatedAt),
})

export const getPaginatedGroups = PaginatedQuery({
  operation: queries.GetPaginatedGroups,
  mapData: ({ paginatedGroups: { groups: results, count } }) => ({
    results,
    count,
  }),
  resultTypename: 'Group',
  pageSize: 20,
  isValidResult: fragments.isGroupFragment,
  filter: (group, { search, groups, contacts }) => {
    if ((groups && groups.length > 0) || (contacts && contacts.length > 0)) {
      throw new IsUnresolved()
    }
    return tokens(search).matchAny(group.name, group.description)
  },
  orderBy: ({ id, name }) => `${name}${id}`,
})

export const getPaginatedRecipients = PaginatedQuery({
  operation: queries.GetPaginatedRecipients,
  mapData: ({ paginatedRecipients: { recipients: results, count } }) => ({
    results,
    count,
  }),
  resultTypename: 'Recipient',
  pageSize: 20,
  isValidResult: fragments.isRecipientFragment,
  filter: () => true,
  orderBy: ({ id }) => `${id}`,
})

export const getPaginatedInvites = PaginatedQuery({
  operation: queries.GetPaginatedInvites,
  mapData: ({ paginatedInvites: { results, count } }) => ({
    results,
    count,
  }),
  resultTypename: 'Invite',
  pageSize: 20,
  isValidResult: fragments.isInviteFragment,
  filter: () => true,
  orderBy: ({ id }) => `${id}`,
})

export const getPhoneNumberCountries = Query({
  operation: queries.GetPhoneNumberCountries,
  mapData: data => data.phoneNumberCountries,
  hitCDNCache: true,
})

export const getPossibleSponsors = ListQuery({
  operation: queries.GetPossibleSponsors,
  mapData: data => data.account.possibleSponsors,
  resultTypename: 'User',
  isValidResult: fragments.isSponsorFragment,
  filter: () => true,
  orderBy: ({ firstName, lastName, id }) => `${lastName}${firstName}${id}`,
})

export const getReminders = ListQuery({
  operation: queries.GetReminders,
  mapData: ({ reminders }) => reminders,
  resultTypename: 'Reminder',
  isValidResult: fragments.isMinimalReminderFragment,
  filter: (reminder, variables) => {
    if (fragments.isMinimalReminderFragment(reminder)) {
      return true
    } else if (objectSize(variables) !== 0) {
      throw new IsUnresolved()
    } else {
      return true
    }
  },
  orderBy: ({ eventDate, id }) => (eventDate ? eventDate : id),
})

export const getSendableCard = Query({
  operation: queries.GetSendableCard,
  mapData: data => data.sendableCard,
})

export const getSendableCards = InfiniteQuery({
  operation: queries.GetSendableCards,
  mapData: data => data.sendableCards,
  resultTypename: 'SendableCard',
  pageSize: 20,
  isValidResult: fragments.isMinimalSendableCardFragment,
  filter: () => true,
  orderBy: ({ id }) => descendingIntegerId(id),
})

export const getProductionInfo = Query({
  operation: queries.GetProductionInfo,
  mapData: data => data.productionInfo,
})

export const getSentCards = PaginatedQuery({
  operation: queries.GetSentCards,
  mapData: ({ paginatedSentCards: { sentCards: results, count } }) => ({
    results,
    count,
  }),
  resultTypename: 'ProductionInfo',
  pageSize: 20,
  isValidResult: fragments.isProductionInfoFragment,
  filter: (
    { dateToSend, recipient, rejectedCards, hold, fulfilled },
    { startDate, endDate, contacts, status, search },
  ) => {
    if (dateToSend && startDate && dateToSend < startDate) {
      return false
    }

    if (dateToSend && endDate && dateToSend > endDate) {
      return false
    }

    if (
      contacts &&
      recipient &&
      recipient.contact &&
      !contacts.includes(recipient.contact.id)
    ) {
      return false
    }

    if (status) {
      if (
        status !== 'PAYMENT_ERROR' &&
        recipient &&
        !recipient.paymentFailure
      ) {
        return false
      }

      if (status === 'REJECTED' && rejectedCards) {
        for (const rejectedCard in rejectedCards) {
          if (!rejectedCard) {
            return false
          }
        }
      } else {
        for (const rejectedCard in rejectedCards) {
          if (!rejectedCard) {
            return true
          }
        }
      }

      if (status === 'HELD' && (!hold || fulfilled)) {
        return false
      } else if (status === 'FULFILLED' && !fulfilled) {
        return false
      } else if (status === 'AWAITING_FULFILLMENT' && (hold || fulfilled)) {
        return false
      } else if (status === 'PENDING' && (hold || fulfilled)) {
        return false
      } else if (status === 'PAYMENT_ERROR' && fulfilled) {
        return false
      }
    }

    return tokens(search).matchAny(
      recipient?.contact?.firstName,
      recipient?.contact?.lastName,
      recipient?.address?.firstName,
      recipient?.address?.lastName,
      recipient?.address?.company,
      recipient?.address?.address1,
      recipient?.address?.city,
      recipient?.address?.state,
      recipient?.address?.postalCode,
      recipient?.address?.country,
      recipient?.card?.sendableCard?.title,
    )
  },
  orderBy: ({ dateToSend, recipient, id }) =>
    `${dateToSend && descendingDateTime(dateToSend)}
    ${recipient?.contact?.lastName ?? ''}
    ${recipient?.contact?.firstName ?? ''}
    ${id}`,
})

export const getSharedCampaigns = ListQuery({
  operation: queries.GetSharedCampaigns,
  mapData: ({ campaignShares }) => campaignShares,
  resultTypename: 'CampaignShare',
  isValidResult: fragments.isCampaignShareFragment,
  filter: (reminder, variables) => {
    if (fragments.isCampaignShareFragment(reminder)) {
      return true
    } else if (objectSize(variables) !== 0) {
      throw new IsUnresolved()
    } else {
      return true
    }
  },
  orderBy: ({ dateShared }) => dateShared,
})

export const getSharedContacts = ListQuery({
  operation: queries.GetSharedContacts,
  mapData: ({ contactShares }) => contactShares,
  resultTypename: 'ContactShare',
  isValidResult: fragments.isContactShareFragment,
  filter: (reminder, variables) => {
    if (fragments.isContactShareFragment(reminder)) {
      return true
    } else if (objectSize(variables) !== 0) {
      throw new IsUnresolved()
    } else {
      return true
    }
  },
  orderBy: ({ createdAt }) => createdAt,
})

export const getSponsor = Query({
  operation: queries.GetSponsor,
  mapData: data => data.sponsor,
})

export const getSponsors = ListQuery({
  operation: queries.GetSponsors,
  mapData: data => data.sponsors,
  resultTypename: 'User',
  isValidResult: fragments.isSponsorFragment,
  filter: (sponsor, { search }) => {
    return tokens(search).matchAny(sponsor.firstName, sponsor.lastName)
  },
  orderBy: ({ firstName, lastName }) => `${lastName}${firstName}`,
})

export const getSponsorBySlug = Query({
  operation: queries.GetStorefront,
  mapData: data => data.user,
})

// WRITE DECLARATIONS IN ALPHABETICAL ORDER
